/**
 * Caisse des patients non couverts (RG-PA-02, spécification du 16/10).
 *
 * Un montant est un nombre ENTIER d'ariary : la base le garde en
 * `Decimal(14, 0)`, l'API l'échange en entier — jamais de flottant, jamais
 * de décimale. Le plafond protège l'entier JavaScript (2^53) bien avant
 * d'être une règle métier.
 */
import { z } from 'zod';
import { MOTIF_ANNULATION_MIN } from '../rules/caisse.js';
/** Au-delà, une saisie est une faute de frappe : dix milliards d'ariary. */
export const MONTANT_MAX_ARIARY = 10_000_000_000;
export const schemaMontantAriary = z
    .number()
    .int('Un montant en ariary est un nombre entier.')
    .min(0)
    .max(MONTANT_MAX_ARIARY);
export const CATEGORIES_TARIF = ['EXTERNE', 'ETRANGER', 'GARDE'];
export const LIBELLE_CATEGORIE_TARIF = {
    EXTERNE: 'Externe',
    ETRANGER: 'Externe étranger',
    GARDE: 'Garde',
};
/**
 * La catégorie d'un reçu : celles de la grille, plus « DENTISTE » (AJ-02),
 * réservée aux reçus d'un prix fixé par le dentiste — jamais proposée dans la
 * grille que la Direction édite (`CATEGORIES_TARIF`).
 */
export const CATEGORIES_RECU = [...CATEGORIES_TARIF, 'DENTISTE', 'FORFAIT'];
export const LIBELLE_CATEGORIE_RECU = {
    ...LIBELLE_CATEGORIE_TARIF,
    DENTISTE: 'Dentiste',
    // Décision 338 — le forfait patient d'une offre (SMIE+ RETRAITE).
    FORFAIT: 'Forfait de l’offre',
};
export const UNITES_TARIF = ['ACTE', 'SEANCE'];
export const LIBELLE_UNITE_TARIF = {
    ACTE: 'à l’acte',
    SEANCE: 'à la séance',
};
/** Décision 338 — le prix d'une ligne dans un centre, quand il diffère du global. */
export const schemaPrixDeCentre = z.object({
    siteId: z.uuid(),
    prix: schemaMontantAriary,
});
/**
 * R7 — une ligne de la grille du tarif. Pour qui la règle : `prix` est le prix
 * global, `prixParCentre` ses exceptions. Pour qui encaisse : `prix` est déjà
 * celui de son centre, et `prixParCentre` est vide.
 */
export const schemaTarifCaisse = z.object({
    id: z.uuid(),
    prestation: z.string(),
    categorie: z.enum(CATEGORIES_TARIF),
    libelle: z.string(),
    prix: schemaMontantAriary,
    unite: z.enum(UNITES_TARIF),
    actif: z.boolean(),
    prixParCentre: z.array(schemaPrixDeCentre),
});
const schemaPrix = schemaMontantAriary.min(1, 'Un prix est strictement positif.');
export const schemaCreationTarif = z.object({
    /** La file à laquelle le reçu donne accès (R8) : majuscules à l'écriture. */
    prestation: z.string().trim().min(1, 'Dites à quelle file la ligne donne accès.').max(80),
    categorie: z.enum(CATEGORIES_TARIF),
    libelle: z.string().trim().min(2, 'Libellé trop court.').max(80),
    prix: schemaPrix,
    unite: z.enum(UNITES_TARIF),
});
/** La prestation et la catégorie ne changent pas : c'est une autre ligne. */
export const schemaModificationTarif = z
    .object({
    libelle: z.string().trim().min(2, 'Libellé trop court.').max(80),
    prix: schemaPrix,
    unite: z.enum(UNITES_TARIF),
    actif: z.boolean(),
})
    .partial()
    .refine((valeur) => Object.keys(valeur).length > 0, {
    message: 'Aucune modification demandée.',
});
/** Décision 338 — poser ou changer le prix d'une ligne pour un centre. */
export const schemaPrixDuCentreDemande = z.object({ prix: schemaPrix });
// ─────────────────────────────────────────────────────────────────────
//  Encaisser et imprimer (PR 2)
// ─────────────────────────────────────────────────────────────────────
export const schemaEncaissementDemande = z.object({
    personneId: z.uuid(),
    /** La ligne de la grille ; absente pour le forfait d'une offre (`forfaitPrestation`). */
    tarifId: z.uuid().optional(),
    /**
     * Décision 338 — un patient couvert dont l'offre fixe un forfait pour cette
     * prestation : le montant vient de l'offre, jamais de la saisie.
     */
    forfaitPrestation: z.string().trim().min(1).max(60).optional(),
    /** Pour une ligne « à la séance » seulement (R6). */
    seances: z.number().int().min(1).max(20).optional(),
    /** La somme que le patient tend (R9). */
    recu: schemaMontantAriary,
    /** Mode dégradé : l'heure notée sur papier pendant une coupure (≤ 7 jours). */
    survenueLe: z.iso.datetime().optional(),
    /**
     * Essai G19 (21/10) — déjà servi aujourd'hui pour cette prestation : la
     * caisse encaisse quand même. L'identifiant du passage montré par le refus.
     */
    confirmeDejaServi: z.uuid().optional(),
    /** Décision 173 — l'attente de la liste que la caissière encaisse (file de la caisse allumée). */
    attenteId: z.uuid().optional(),
});
/** R10 — tout ce que le reçu imprime ; les montants sont FIGÉS à l'encaissement. */
export const schemaRecu = z.object({
    id: z.uuid(),
    numero: z.string(),
    etablissement: z.string(),
    centre: z.object({ id: z.uuid(), nom: z.string() }),
    encaisseLe: z.iso.datetime(),
    /** Mode dégradé : l'heure du papier ; le reçu dit « ressaisi ». */
    survenueLe: z.iso.datetime().nullable(),
    patient: z.object({
        id: z.uuid(),
        nom: z.string(),
        prenom: z.string().nullable(),
        matricule: z.string().nullable(),
    }),
    prestation: z.string(),
    libelle: z.string(),
    /** La catégorie de la ligne de la grille ; `DENTISTE` pour un prix fixé par le dentiste (AJ-02). */
    categorie: z.enum(CATEGORIES_RECU),
    unite: z.enum(UNITES_TARIF),
    prixUnitaire: schemaMontantAriary,
    seances: z.number().int(),
    montant: schemaMontantAriary,
    recu: schemaMontantAriary,
    rendu: schemaMontantAriary,
    caissiere: z.object({ id: z.uuid(), nom: z.string(), prenom: z.string() }),
    /** Posé à l'entrée en file : le reçu a servi de ticket (R2). */
    visiteId: z.uuid().nullable(),
    /**
     * Décision 60 — le patient a été appelé et marqué absent : le reçu reste
     * valable pour revenir, à tout moment, et ne se rembourse pas.
     */
    absentALAppel: z.boolean(),
    /**
     * Le praticien a pris le patient (ou l'a déjà reçu) : le reçu ne s'annule
     * plus (R12) — l'écran ne propose pas « Annuler ».
     */
    consultationCommencee: z.boolean(),
    annuleLe: z.iso.datetime().nullable(),
    motifAnnulation: z.string().nullable(),
    /** Vrai pour une réimpression (R10). */
    duplicata: z.boolean(),
    /** Décision 173 — à l'encaissement, le patient est entré dans cette file, à ce rang (1 = le prochain). */
    entree: z
        .object({
        prestation: z.string(),
        rang: z.number().int(),
        /** Numéros de passage (décision 284) : celui de l'entrée, à remettre au patient. */
        numeroPassage: z.string().nullable(),
        /** L'heure d'arrivée de la visite, imprimée sur le ticket (décision 283). */
        arriveeLe: z.iso.datetime(),
    })
        .nullable()
        .optional(),
    /**
     * AJ-02 — un reçu d'un prix fixé par le dentiste : ses prestations réalisées
     * et le dentiste, imprimés sur le reçu. Absent ou `null` : un reçu de la grille.
     */
    prixDentiste: z
        .object({
        prestations: z.array(z.string()),
        dentiste: z.object({ id: z.uuid(), nom: z.string(), prenom: z.string() }),
    })
        .nullable()
        .optional(),
});
export const SITUATIONS_CAISSE = ['A_CONFIRMER', 'COUVERT', 'A_REGLER'];
/** Ce que la caisse sait d'une fiche, sans rien de médical (R3, R4, R5). */
export const schemaSituationCaisse = z.object({
    situation: z.enum(SITUATIONS_CAISSE),
    /** Fiche à confirmer : depuis quand elle attend la chargée. */
    enAttenteDepuis: z.iso.datetime().nullable(),
    /** Les reçus valides du jour, encore inutilisés, au centre du poste. */
    recusDuJour: z.array(schemaRecu),
    /**
     * Décisions 48, 68 — les reçus papier d'une coupure, ressaisis par la
     * caisse du centre et pas encore liés à une entrée : l'entrée ressaisie
     * d'un non-couvert présente celui du jour de la coupure.
     */
    recusRessaisis: z.array(schemaRecu),
    /**
     * Décision 338 — patient couvert : l'offre qui restreint ses prestations
     * (`null` : aucune restriction) et les forfaits qu'il paie à la caisse.
     */
    offre: z
        .object({
        nom: z.string(),
        prestations: z.array(z.object({ prestation: z.string(), forfait: z.number().int().nullable() })),
    })
        .nullable()
        .optional(),
});
/**
 * Un jour civil `AAAA-MM-JJ` (heure de Madagascar) qui existe : le
 * 31 février est refusé ici (400), pas en base (revue S67b, à corriger 4).
 */
const schemaJour = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Une date AAAA-MM-JJ.')
    .refine((jour) => {
    const date = new Date(`${jour}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === jour;
}, 'Cette date n’existe pas.');
const schemaPersonnel = z.object({ id: z.uuid(), nom: z.string(), prenom: z.string() });
/**
 * R13, décision 61 — LA caisse du centre un jour (aujourd'hui par défaut) :
 * les reçus de toutes ses caissières et ses totaux.
 */
export const schemaJourneeCaisse = z.object({
    jour: z.string(),
    ouverte: z.boolean(),
    /** Qui a fait le premier mouvement du jour ; `null` si rien encore. */
    ouvertePar: schemaPersonnel.nullable(),
    clotureeLe: z.iso.datetime().nullable(),
    /** Décision 61 — la caissière du centre qui a clôturé. */
    clotureePar: schemaPersonnel.nullable(),
    recus: z.array(schemaRecu),
    nombre: z.number().int(),
    totalEncaisse: schemaMontantAriary,
    /** Les reçus annulés AVANT la clôture : ceux d'après sont des sorties du jour de l'annulation. */
    totalAnnule: schemaMontantAriary,
    /**
     * Décisions 52, 56, 61 — les reçus annulés par la Direction après clôture
     * rendus au patient ce jour-là par une caissière du centre : l'argent est
     * sorti de cette caisse-ci.
     */
    sorties: z.array(schemaRecu),
    totalSorties: schemaMontantAriary,
    /** Encaissé − annulé − sorties : négatif si l'on a rendu plus qu'encaissé. */
    attendu: z.number().int(),
    /** Clôturée : les espèces comptées, l'écart et son commentaire. */
    compte: schemaMontantAriary.nullable(),
    ecart: z.number().int().nullable(),
    commentaire: z.string().nullable(),
    /** R13 — les caisses du centre d'un jour passé restées ouvertes, à clôturer. */
    enRetard: z.array(z.string()),
    /**
     * Décision 48 — les caisses d'un jour passé ouvertes AUJOURD'HUI par une
     * ressaisie après coupure : pas encore « oubliées » (comme l'alerte R13),
     * la caissière finit de ressaisir puis les clôture.
     */
    enRessaisie: z.array(z.string()),
});
export const schemaFiltreJournee = z.object({ jour: schemaJour.optional() });
/** R12 — annuler un reçu : un motif lisible, toujours. */
export const schemaAnnulationEncaissement = z
    .object({
    motif: z
        .string()
        .trim()
        .min(MOTIF_ANNULATION_MIN, `Un motif de ${String(MOTIF_ANNULATION_MIN)} caractères au moins.`)
        .max(300),
})
    .strict();
/** R13, décision 61 — clôturer la caisse du centre d'un jour : les espèces comptées, hors fond. */
export const schemaClotureCaisse = z
    .object({
    /** Aujourd'hui par défaut ; un jour passé pour une caisse oubliée. */
    jour: schemaJour.optional(),
    compte: schemaMontantAriary,
    commentaire: z.string().trim().max(500).optional(),
})
    .strict();
// ─────────────────────────────────────────────────────────────────────
//  Le registre de la Direction (PR 5, R14 ; décisions 52, 56, 68)
// ─────────────────────────────────────────────────────────────────────
/** Une période plus longue se lit en plusieurs fois : la réponse reste courte. */
export const REGISTRE_CAISSE_JOURS_MAX = 93;
/** R14 — la période (jours de caisse, heure de Madagascar), le centre et la caissière. */
export const schemaFiltreRegistreCaisse = z
    .object({
    du: schemaJour,
    au: schemaJour,
    siteId: z.uuid().optional(),
    caissiereId: z.uuid().optional(),
})
    .refine((f) => f.du <= f.au, { message: 'La fin de la période précède son début.', path: ['au'] })
    .refine((f) => (Date.parse(`${f.au}T00:00:00Z`) - Date.parse(`${f.du}T00:00:00Z`)) / 86_400_000 <
    REGISTRE_CAISSE_JOURS_MAX, {
    message: `Une période de ${String(REGISTRE_CAISSE_JOURS_MAX)} jours au plus.`,
    path: ['du'],
});
/** L'état d'un reçu : la règle est `etatDuRecu` (`rules/caisse.ts`). */
export const ETATS_RECU_REGISTRE = [
    'VALIDE',
    'ANNULE',
    'A_RENDRE',
    'RENDU',
];
/** Ce que l'écran et l'export écrivent pour chaque état. */
export const LIBELLE_ETAT_RECU = {
    VALIDE: 'Valable',
    ANNULE: 'Annulé',
    A_RENDRE: 'À rendre',
    RENDU: 'Rendu',
};
export const schemaLigneRegistreCaisse = schemaRecu.extend({
    /** Le jour de la caisse qui a reçu l'argent (celui de la coupure pour une ressaisie). */
    jour: z.string(),
    etat: z.enum(ETATS_RECU_REGISTRE),
    /** La caisse de ce jour est clôturée : seule la Direction annule encore. */
    caisseCloturee: z.boolean(),
    /** La consultation a commencé : le reçu ne s'annule plus (R12). */
    consultationCommencee: z.boolean(),
    /** Rendu : le jour de la caisse d'où l'argent est sorti. */
    renduLe: z.string().nullable(),
});
const schemaCentre = z.object({ id: z.uuid(), nom: z.string() });
/** La caisse d'un centre un jour, avec ses totaux et, clôturée, son écart. */
export const schemaCaisseDuRegistre = z.object({
    id: z.uuid(),
    jour: z.string(),
    centre: schemaCentre,
    clotureeLe: z.iso.datetime().nullable(),
    clotureePar: schemaPersonnel.nullable(),
    nombre: z.number().int(),
    totalEncaisse: schemaMontantAriary,
    totalAnnule: schemaMontantAriary,
    totalSorties: schemaMontantAriary,
    attendu: z.number().int(),
    compte: schemaMontantAriary.nullable(),
    ecart: z.number().int().nullable(),
    commentaire: z.string().nullable(),
});
/** R13 — une caisse d'un jour passé jamais clôturée, quelle que soit la période choisie. */
export const schemaCaisseNonCloturee = z.object({
    id: z.uuid(),
    jour: z.string(),
    centre: schemaCentre,
    nombre: z.number().int(),
    attendu: z.number().int(),
    /** L'alerte du lendemain est partie (la cloche y mène). */
    signaleeLe: z.iso.datetime().nullable(),
});
export const schemaRegistreCaisse = z.object({
    du: z.string(),
    au: z.string(),
    /** Les centres que le compte peut lire : tous pour la Direction, le sien sinon. */
    centres: z.array(schemaCentre),
    /** Les caissières qui ont encaissé sur la période, dans ces centres. */
    caissieres: z.array(schemaPersonnel),
    recus: z.array(schemaLigneRegistreCaisse),
    /** Le nombre exact de reçus de la sélection, au-delà de ceux affichés. */
    nombreRecus: z.number().int(),
    /** Au-delà de la limite d'affichage, la liste est coupée (le compte reste exact). */
    tronque: z.boolean(),
    /** La sélection (caissière comprise) : encaissé, annulé avant clôture. */
    totalEncaisse: schemaMontantAriary,
    totalAnnule: schemaMontantAriary,
    /** Les caisses de la période, par jour et par centre (la caissière ne les filtre pas). */
    caisses: z.array(schemaCaisseDuRegistre),
    /**
     * Les reçus rendus au patient depuis une caisse de la période — par le
     * centre : on ne sait pas quelle caissière a rendu, le filtre ne s'y applique pas.
     */
    sorties: z.array(schemaLigneRegistreCaisse),
    /** Tous les reçus encore à rendre dans ces centres, quelle que soit la période. */
    aRendre: z.array(schemaLigneRegistreCaisse),
    nonCloturees: z.array(schemaCaisseNonCloturee),
});
//# sourceMappingURL=caisse.js.map